import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Download, FolderOpen, Cloud, Check, AlertCircle, Loader2, ArrowRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Normalize a track title/artist for fuzzy matching against filenames.
const normalize = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/\.(mp3|m4a|wav|flac|aiff?|aac|ogg)$/i, '')
    .replace(/[\u2018\u2019\u201A\u201B\u201C\u201D\u201E\u201F]/g, "'") // smart quotes
    .replace(/\([^)]*\)/g, ' ')   // (Extended Mix), (Original Mix), etc.
    .replace(/\[[^\]]*\]/g, ' ')  // [bracket tags]
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Token-set similarity: how many of t1's words also appear in t2.
const tokenScore = (a, b) => {
  if (!a || !b) return 0;
  const setA = new Set(a.split(' ').filter(w => w.length > 1));
  const setB = new Set(b.split(' ').filter(w => w.length > 1));
  if (!setA.size || !setB.size) return 0;
  let hits = 0;
  setA.forEach(w => { if (setB.has(w)) hits++; });
  return hits / Math.max(setA.size, setB.size);
};

// Match a manifest track against the user's folder file list.
// Returns the best candidate {file, score} or {file: null, score: 0}.
const bestMatch = (track, files) => {
  const targetA = normalize(`${track.artist} ${track.title}`);
  const targetB = normalize(`${track.title} ${track.artist}`);
  let best = { file: null, score: 0 };
  for (const f of files) {
    const fname = normalize(f.name);
    const score = Math.max(tokenScore(targetA, fname), tokenScore(targetB, fname));
    if (score > best.score) best = { file: f, score };
  }
  return best;
};

export default function ExportModal({ kind, id, name, onClose }) {
  const [step, setStep] = useState('mode');  // mode → download | local
  const [manifest, setManifest] = useState(null);
  const [loadingManifest, setLoadingManifest] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Mode B (local) state
  const [folderName, setFolderName] = useState('');
  const [folderPath, setFolderPath] = useState('');
  const [folderFiles, setFolderFiles] = useState([]);
  const [matches, setMatches] = useState({}); // track_id -> {file, score, status}
  const folderInputRef = useRef(null);

  // Load the export manifest as soon as the modal opens.
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/export/${kind}/${id}/manifest`);
        if (!abort) setManifest(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to load tracks');
        onClose();
      } finally {
        if (!abort) setLoadingManifest(false);
      }
    })();
    return () => { abort = true; };
  }, [kind, id, onClose]);

  // ─── Mode A: per-track download via R2 presigned URLs ────────────
  // We had previously tried streaming a single zip through our backend
  // but Cloudflare's proxy was cutting the long-running connection on
  // large playlists. Going directly to R2 with short-lived presigned
  // URLs sidesteps every middlebox timeout — each file is a fresh,
  // small request the browser can resume/retry on its own.
  const [downloadProgress, setDownloadProgress] = useState({ done: 0, total: 0, current: '' });

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // Trigger a download via a hidden anchor. Some browsers will only
  // honor one programmatic download per click without a small delay.
  const triggerDownload = (href, filename) => {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const exportDownload = async () => {
    if (!manifest) return;

    // ① Ask the browser to pick a target folder via the File System
    //    Access API. Chrome / Edge / Opera support this. Safari/Firefox
    //    don't — those users fall through to the old "stream to Downloads
    //    folder via <a download>" path.
    let dirHandle = null;
    const supportsFSA = typeof window.showDirectoryPicker === 'function';
    if (supportsFSA) {
      try {
        dirHandle = await window.showDirectoryPicker({
          id: 'cratelab-export',
          mode: 'readwrite',
          startIn: 'music',
        });
      } catch (err) {
        // User cancelled the picker — abort cleanly. Don't fall back to
        // Downloads folder because they explicitly opted into picking
        // a destination.
        if (err?.name === 'AbortError') return;
        // Permission denied / unsupported context → fall through
        dirHandle = null;
      }
    }

    setExporting(true);
    try {
      const token = localStorage.getItem('cratelab_token');
      const r = await fetch(`${API}/export/${kind}/${id}/presigned`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) {
        let detail = 'Export failed';
        try { detail = (await r.json()).detail || detail; } catch { /* not json */ }
        throw new Error(detail);
      }
      const data = await r.json();
      const items = (data.items || []).filter((i) => i.url);
      const missing = (data.items || []).length - items.length;

      // ② Helper to write a Blob into the picked folder.
      const writeToFolder = async (filename, blob) => {
        const handle = await dirHandle.getFileHandle(filename, { create: true });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      };

      // ③ Write the m3u8 first — instant.
      const m3uBlob = new Blob([data.m3u8], { type: 'audio/x-mpegurl' });
      if (dirHandle) {
        await writeToFolder(data.m3u8_filename, m3uBlob);
      } else {
        const m3uUrl = URL.createObjectURL(m3uBlob);
        triggerDownload(m3uUrl, data.m3u8_filename);
        await sleep(400);
        URL.revokeObjectURL(m3uUrl);
      }

      // ④ Then each mp3, one at a time, with live progress.
      setDownloadProgress({ done: 0, total: items.length, current: '' });
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        setDownloadProgress({ done: i, total: items.length, current: item.filename });
        if (dirHandle) {
          try {
            const resp = await fetch(item.url);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            await writeToFolder(item.filename, blob);
          } catch (err) {
            // Skip this track but keep going — log it for the user.
            toast.error(`${item.filename}: ${err.message || 'fetch failed'}`);
          }
        } else {
          triggerDownload(item.url, item.filename);
          // 600 ms pacing — tested to keep Chrome's download queue happy
          await sleep(600);
        }
      }
      setDownloadProgress({ done: items.length, total: items.length, current: '' });

      const where = dirHandle ? 'into your chosen folder' : 'to your Downloads folder';
      if (missing > 0) {
        toast.success(`${items.length} tracks saved ${where}. ${missing} weren't synced to the cloud yet.`);
      } else {
        toast.success(`${items.length} tracks + m3u8 saved ${where}`);
      }
      // Leave the modal open ~1s so the user sees the progress complete
      setTimeout(onClose, 1000);
    } catch (err) {
      toast.error(err?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // ─── Mode B: choose local folder, fuzzy-match, build m3u8 ────────
  const onFolderPicked = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // Filter to audio extensions only
    const audio = files.filter(f =>
      /\.(mp3|m4a|wav|flac|aiff?|aac|ogg)$/i.test(f.name)
    );
    if (!audio.length) {
      toast.error('That folder has no audio files');
      return;
    }
    setFolderFiles(audio);
    // Best-effort: pull the top-level folder name from webkitRelativePath
    const first = audio[0].webkitRelativePath || audio[0].name;
    const topFolder = first.split('/')[0] || 'My Tracks';
    setFolderName(topFolder);
    // Run fuzzy match
    const next = {};
    (manifest?.tracks || []).forEach(t => {
      const m = bestMatch(t, audio);
      next[t.id] = {
        file: m.file,
        score: m.score,
        status: m.score >= 0.5 ? 'matched' : 'missing',
      };
    });
    setMatches(next);
  };

  const matchedCount = Object.values(matches).filter(m => m.status === 'matched').length;
  const missingCount = (manifest?.count || 0) - matchedCount;

  const exportLocal = async () => {
    if (!manifest) return;
    if (!folderPath.trim()) {
      toast.error('Enter your folder path so the m3u8 can find the files');
      return;
    }
    setExporting(true);
    try {
      // Map track_id -> matched filename (or keep server fallback for missing)
      const trackFilenames = {};
      Object.entries(matches).forEach(([tid, m]) => {
        if (m.file) trackFilenames[tid] = m.file.name;
      });
      const isWindows = /\\|^[a-zA-Z]:/.test(folderPath);
      const sep = isWindows ? '\\' : '/';
      const res = await axios.post(
        `${API}/export/${kind}/${id}`,
        {
          download_audio: false,
          local_path_prefix: folderPath.trim(),
          path_separator: sep,
          track_filenames: trackFilenames,
        },
        { responseType: 'text' }
      );
      const blob = new Blob([res.data], { type: 'audio/x-mpegurl' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${manifest.name.replace(/[^a-z0-9]/gi, '_')}.m3u8`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('M3U8 saved — drop it into Rekordbox');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const fallbackDownloadMissing = async () => {
    // User wants the zip with the missing tracks bundled
    setStep('download');
    await exportDownload();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="export-modal-backdrop"
      >
        <motion.div
          initial={{ y: 20, scale: 0.97, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 10, scale: 0.98, opacity: 0 }}
          className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col shadow-[0_24px_64px_rgba(15,23,42,0.18)]"
          onClick={(e) => e.stopPropagation()}
          data-testid="export-modal"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between shrink-0">
            <div>
              <p className="font-mono text-[10px] tracking-[0.18em] text-[#2563FF] mb-1">EXPORT {kind.toUpperCase()}</p>
              <h2 className="font-heading text-lg text-[#0F172A]">{manifest?.name || name || 'Loading…'}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full hover:bg-[#F1F5F9] flex items-center justify-center text-[#64748B]"
              data-testid="export-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {loadingManifest && (
              <div className="py-12 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-[#2563FF]" /></div>
            )}

            {/* Step 1: choose mode */}
            {!loadingManifest && step === 'mode' && (
              <div className="space-y-3" data-testid="export-mode-choice">
                <p className="text-sm text-[#475569] mb-4">
                  {manifest?.count || 0} tracks. How do you want to export them?
                </p>
                <button
                  onClick={() => setStep('download')}
                  className="w-full text-left p-4 rounded-xl border border-[#E2E8F0] hover:border-[#2563FF]/40 hover:bg-[#EFF6FF]/40 transition-colors flex items-start gap-4"
                  data-testid="export-mode-download"
                >
                  <div className="w-11 h-11 rounded-xl gradient-primary text-white flex items-center justify-center shrink-0">
                    <Cloud className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-heading text-base text-[#0F172A]">Download tracks from CrateLab</p>
                    <p className="text-xs text-[#64748B] mt-1 leading-relaxed">
                      Browser downloads each .mp3 individually plus the .m3u8 — drop them into one folder and Rekordbox will find everything.
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#94A3B8] mt-3 shrink-0" />
                </button>

                <button
                  onClick={() => setStep('local')}
                  className="w-full text-left p-4 rounded-xl border border-[#E2E8F0] hover:border-[#2563FF]/40 hover:bg-[#EFF6FF]/40 transition-colors flex items-start gap-4"
                  data-testid="export-mode-local"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#EFF6FF] text-[#2563FF] flex items-center justify-center shrink-0">
                    <FolderOpen className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-heading text-base text-[#0F172A]">I already have these tracks locally</p>
                    <p className="text-xs text-[#64748B] mt-1 leading-relaxed">
                      Point to a folder on your machine. We'll match each track to your local file and write an .m3u8 with those paths.
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#94A3B8] mt-3 shrink-0" />
                </button>
              </div>
            )}

            {/* Step 2a: download confirmation */}
            {!loadingManifest && step === 'download' && (
              <div data-testid="export-download-step">
                <div className="p-4 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] mb-4">
                  <p className="font-mono text-[10px] tracking-wider text-[#94A3B8] mb-2">YOU'LL GET</p>
                  <p className="text-sm text-[#0F172A] font-mono">
                    {manifest.name.replace(/[^a-z0-9]/gi, '_')}.m3u8<br />
                    <span className="text-[#64748B]">+ {manifest.count} individual .mp3 files</span>
                  </p>
                </div>
                <p className="text-xs text-[#64748B] mb-4">
                  On Chrome/Edge you'll pick a folder first and every file lands there. Safari/Firefox get them in your Downloads folder. Drop the .m3u8 into Rekordbox once everything's downloaded.
                </p>
                {(() => {
                  const missingAudio = (manifest.tracks || []).filter(t => !t.has_audio).length;
                  if (missingAudio === 0) return null;
                  return (
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 mb-4 flex gap-2 items-start">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <p className="text-xs text-amber-900">
                        {missingAudio} of {manifest.count} tracks don't have cloud audio yet, so they'll be marked
                        <span className="font-mono"> #MISSING</span> in the .m3u8. Upload the audio files via the Upload page for a complete export.
                      </p>
                    </div>
                  );
                })()}
                {exporting && downloadProgress.total > 0 && (
                  <div className="p-3 rounded-lg bg-[#EFF6FF] border border-[#2563FF]/20 mb-4">
                    <div className="flex items-center justify-between text-xs text-[#0F172A] mb-2">
                      <span className="font-mono">{downloadProgress.done} / {downloadProgress.total}</span>
                      <span className="text-[#64748B] truncate ml-2">{downloadProgress.current}</span>
                    </div>
                    <div className="w-full h-1.5 bg-white rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#2563FF] transition-all duration-300"
                        style={{ width: `${(downloadProgress.done / Math.max(downloadProgress.total, 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={() => setStep('mode')} className="btn-secondary flex-1" data-testid="export-back">Back</button>
                  <button
                    onClick={exportDownload}
                    disabled={exporting}
                    className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="export-download-start"
                  >
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    {exporting ? 'Downloading…' : 'Start download'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2b: local folder picker + verify */}
            {!loadingManifest && step === 'local' && (
              <div data-testid="export-local-step">
                {!folderFiles.length && (
                  <>
                    <p className="text-sm text-[#475569] mb-4">
                      Pick the folder containing your existing audio files. We'll fuzzy-match each track in this {kind} against the filenames in there.
                    </p>
                    <input
                      ref={folderInputRef}
                      type="file"
                      webkitdirectory=""
                      directory=""
                      multiple
                      className="hidden"
                      onChange={onFolderPicked}
                      data-testid="export-folder-input"
                    />
                    <button
                      onClick={() => folderInputRef.current?.click()}
                      className="w-full p-6 rounded-xl border-2 border-dashed border-[#E2E8F0] hover:border-[#2563FF]/50 transition-colors text-center"
                      data-testid="export-pick-folder"
                    >
                      <FolderOpen className="w-8 h-8 mx-auto text-[#94A3B8] mb-2" />
                      <p className="font-heading text-sm text-[#0F172A]">Choose folder</p>
                      <p className="text-xs text-[#64748B] mt-1">We'll scan it for matching audio files</p>
                    </button>
                    <p className="text-[11px] text-[#94A3B8] mt-3 leading-relaxed">
                      Chrome will say "Upload" — that's just the OS dialog label. Nothing is uploaded;
                      your audio files never leave your computer. We only read filenames locally.
                    </p>
                  </>
                )}

                {folderFiles.length > 0 && (
                  <>
                    <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] mb-3 flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-[#2563FF]" />
                      <p className="text-sm text-[#0F172A] truncate flex-1">{folderName} · {folderFiles.length} audio files</p>
                      <button
                        onClick={() => { setFolderFiles([]); setMatches({}); setFolderName(''); }}
                        className="text-xs text-[#2563FF] hover:underline"
                        data-testid="export-folder-change"
                      >
                        Change
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-200 text-center">
                        <p className="text-xl font-heading text-emerald-700">{matchedCount}</p>
                        <p className="text-[10px] font-mono uppercase tracking-wider text-emerald-700/70">Matched</p>
                      </div>
                      <div className={`p-2 rounded-lg text-center border ${missingCount ? 'bg-amber-50 border-amber-200' : 'bg-[#F8FAFC] border-[#E2E8F0]'}`}>
                        <p className={`text-xl font-heading ${missingCount ? 'text-amber-700' : 'text-[#0F172A]'}`}>{missingCount}</p>
                        <p className={`text-[10px] font-mono uppercase tracking-wider ${missingCount ? 'text-amber-700/70' : 'text-[#94A3B8]'}`}>Missing</p>
                      </div>
                    </div>

                    {missingCount > 0 && (
                      <div className="max-h-32 overflow-y-auto mb-3 p-2 rounded-lg bg-amber-50/60 border border-amber-200 space-y-1">
                        {(manifest.tracks || []).filter(t => matches[t.id]?.status !== 'matched').slice(0, 10).map(t => (
                          <p key={t.id} className="text-xs text-amber-900 truncate flex items-center gap-1">
                            <X className="w-3 h-3 shrink-0" />
                            {t.artist ? `${t.artist} – ${t.title}` : t.title}
                          </p>
                        ))}
                        {missingCount > 10 && <p className="text-[10px] text-amber-700/70 pl-4">…and {missingCount - 10} more</p>}
                      </div>
                    )}

                    <label className="block text-xs font-mono text-[#64748B] uppercase tracking-wider mb-1">
                      Folder path on your machine
                    </label>
                    <input
                      type="text"
                      value={folderPath}
                      onChange={(e) => setFolderPath(e.target.value)}
                      placeholder="C:\Users\HP\Music\My Tracks  (or  /Users/me/Music/My Tracks)"
                      className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] focus:border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/15 outline-none text-sm font-mono mb-2"
                      data-testid="export-folder-path"
                    />
                    <p className="text-[11px] text-[#94A3B8] mb-4 leading-relaxed">
                      Required — the browser can't see your absolute path for security reasons, so we need it from you. The m3u8 file will use it for every track.
                    </p>

                    <div className="flex gap-2">
                      <button onClick={() => setStep('mode')} className="btn-secondary flex-1" data-testid="export-back">Back</button>
                      {missingCount > 0 && (
                        <button
                          onClick={fallbackDownloadMissing}
                          disabled={exporting}
                          className="btn-secondary flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
                          data-testid="export-fallback-download"
                          title="Build a zip with the missing tracks from the cloud"
                        >
                          <Cloud className="w-4 h-4" />
                          Download from cloud
                        </button>
                      )}
                      <button
                        onClick={exportLocal}
                        disabled={exporting || !folderPath.trim() || matchedCount === 0}
                        className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
                        data-testid="export-local-start"
                      >
                        {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        Save m3u8
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
